import type { closestCenter } from "@dnd-kit/core";

export const GAP_ID_PREFIX = "gap-";

/** Wie weit über und unter der Mitte eines Zwischenraums noch dieser getroffen wird. */
const GAP_TOLERANCE_PX = 18;
const CENTER_DIVISOR = 2;
const VERTICAL_DISTANCE_WEIGHT = 4;

type CollisionArgs = Parameters<typeof closestCenter>[0];
type Collisions = ReturnType<typeof closestCenter>;
type DroppableId = CollisionArgs["droppableContainers"][number]["id"];
type Point = { x: number; y: number };
type Box = { top: number; left: number; width: number; height: number };

/** Seite der Zielkarte, an der die gezogene Karte eingefügt wird. */
export type DashboardDropSide = "before" | "after";

export function isDashboardGapId(id: string): boolean {
  return id.startsWith(GAP_ID_PREFIX);
}

/**
 * Trefferermittlung für das Karten-Gitter im Layoutmodus. Maßgeblich ist allein der Zeiger, nie das
 * Rechteck der gezogenen Karte — eine breite Karte überdeckt sonst halbe Zeilen.
 *
 * 1. **Karte auf Zeigerhöhe.** Liegt der Zeiger in der Höhe einer Zeile, ist eine Karte dieser Zeile
 *    gemeint: die unter dem Zeiger, im Spalt zwischen zwei Karten die nächstgelegene. Die gezogene
 *    Karte zählt mit — über ihrem eigenen Platz bleibt alles, wie es ist.
 * 2. **Zwischenraum.** Zwischen zwei Zeilen der Zwischenraum in der Nähe; er erzeugt eine neue Zeile.
 * 3. **Ober- und unterhalb aller Karten** der äußerste Zwischenraum.
 * 4. Sonst die nächstgelegene Karte (z. B. im Spalt zwischen untereinander gestapelten Karten).
 *
 * Die Funktion ist zustandslos und liefert für denselben Zeiger immer dasselbe Ziel. Das Gitter
 * verschiebt sich während des Ziehens nicht, die zu Beginn gemessenen Rechtecke bleiben also gültig.
 */
export function dashboardCollision(args: CollisionArgs): Collisions {
  const { droppableRects, droppableContainers } = args;
  const pointer = dashboardDragPoint(args);

  const cards: { id: DroppableId; rect: Box }[] = [];
  const gaps: { id: DroppableId; centerY: number }[] = [];
  for (const container of droppableContainers) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    if (!isDashboardGapId(String(container.id))) cards.push({ id: container.id, rect });
    else if (!container.disabled) gaps.push({ id: container.id, centerY: rect.top + rect.height / CENTER_DIVISOR });
  }

  const horizontalDistance = (rect: Box) => Math.max(0, rect.left - pointer.x, pointer.x - (rect.left + rect.width));
  const verticalDistance = (rect: Box) => Math.max(0, rect.top - pointer.y, pointer.y - (rect.top + rect.height));
  function nearest<T>(items: T[], distance: (item: T) => number): T | null {
    let best: T | null = null;
    let bestDistance = Infinity;
    for (const item of items) {
      const d = distance(item);
      if (d < bestDistance) { best = item; bestDistance = d; }
    }
    return best;
  }

  const cardInRow = nearest(cards.filter((card) => verticalDistance(card.rect) === 0), (card) => horizontalDistance(card.rect));
  if (cardInRow) return [{ id: cardInRow.id }];

  const gap = nearest(gaps, (entry) => Math.abs(pointer.y - entry.centerY));
  if (gap && Math.abs(pointer.y - gap.centerY) < GAP_TOLERANCE_PX) return [{ id: gap.id }];

  if (cards.length > 0 && gaps.length > 0) {
    const first = nearest(gaps, (entry) => entry.centerY)!;
    const last = nearest(gaps, (entry) => -entry.centerY)!;
    const top = Math.min(...cards.map((card) => card.rect.top));
    const bottom = Math.max(...cards.map((card) => card.rect.top + card.rect.height));
    if (pointer.y < top && first.centerY <= top) return [{ id: first.id }];
    if (pointer.y > bottom && last.centerY >= bottom) return [{ id: last.id }];
  }

  // Vertikaler Abstand wiegt schwerer: im Zweifel bleibt das Ziel in der Zeile, auf deren Höhe man ist.
  const card = nearest(cards, (entry) => verticalDistance(entry.rect) * VERTICAL_DISTANCE_WEIGHT + horizontalDistance(entry.rect));
  return card ? [{ id: card.id }] : [];
}

/**
 * Bezugspunkt des Ziehens: der Zeiger, beim Ziehen per Tastatur (ohne Zeiger) die Mitte der
 * gezogenen Karte.
 */
export function dashboardDragPoint(args: Pick<CollisionArgs, "pointerCoordinates" | "collisionRect">): Point {
  const { pointerCoordinates, collisionRect } = args;
  return pointerCoordinates ?? { x: collisionRect.left + collisionRect.width / CENTER_DIVISOR, y: collisionRect.top + collisionRect.height / CENTER_DIVISOR };
}

/**
 * Einfügeseite an einer Zielkarte: links/rechts der Mitte bei Karten nebeneinander, oberhalb/unterhalb
 * bei untereinander gestapelten Karten (schmale Ansicht).
 */
export function dashboardDropSide(rect: Box, pointer: Point, stacked: boolean): DashboardDropSide {
  return stacked
    ? (pointer.y < rect.top + rect.height / CENTER_DIVISOR ? "before" : "after")
    : (pointer.x < rect.left + rect.width / CENTER_DIVISOR ? "before" : "after");
}
